import { z } from 'zod';

import {
  emailSchema,
  idSchema,
  isoDateTimeSchema,
  okResponseSchema,
  passwordSchema,
} from '../common';
import { userRoleSchema, userStatusSchema } from '../enums';

/**
 * `/auth/*` DTOs.
 *
 * Two secrets cross this boundary and neither is ever persisted in the clear:
 * the password (scrypt, see DECISIONS.md) and the refresh token (stored as a
 * hash, rotated on every use). Neither field may be written to a log — the Pino
 * redaction list in `packages/logger` covers `password`, `accessToken` and
 * `refreshToken` precisely because they appear in these shapes.
 */

/** Human-facing name. Trimmed so " " cannot pass as a name. */
export const displayNameSchema = z.string().trim().min(1).max(120);

// ---------------------------------------------------------------------------
// Tokens & identity
// ---------------------------------------------------------------------------

export const tokenPairSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  /** Access-token lifetime in seconds; the dashboard refreshes before it lapses. */
  expiresIn: z.number().int().positive(),
  tokenType: z.literal('Bearer'),
});
export type TokenPair = z.infer<typeof tokenPairSchema>;

export const authUserSummarySchema = z.object({
  id: idSchema,
  email: emailSchema,
  displayName: z.string(),
  role: userRoleSchema,
  status: userStatusSchema,
  /** Every user belongs to exactly one workspace in the MVP. */
  workspaceId: idSchema,
  emailVerifiedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});
export type AuthUserSummary = z.infer<typeof authUserSummarySchema>;

export const authSessionResponseSchema = z.object({
  user: authUserSummarySchema,
  tokens: tokenPairSchema,
});
export type AuthSessionResponse = z.infer<typeof authSessionResponseSchema>;

// ---------------------------------------------------------------------------
// POST /auth/register
// ---------------------------------------------------------------------------

export const registerRequestSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  displayName: displayNameSchema,
  /** Names the workspace created together with the first user of an account. */
  workspaceName: z.string().trim().min(1).max(120).optional(),
  /** A literal `true` — an unchecked box must fail validation, not default. */
  acceptedTerms: z.literal(true),
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const registerResponseSchema = authSessionResponseSchema.extend({
  /**
   * Email verification is a stub in the MVP: the account is usable immediately
   * and the flag exists so the UI states that fact instead of implying a mail
   * was sent that nothing will ever send.
   */
  emailVerificationRequired: z.boolean(),
});
export type RegisterResponse = z.infer<typeof registerResponseSchema>;

// ---------------------------------------------------------------------------
// POST /auth/login
// ---------------------------------------------------------------------------

export const loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
  /** Free-text label for the session list; never used for authorisation. */
  deviceName: z.string().trim().max(120).optional(),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const loginResponseSchema = authSessionResponseSchema;
export type LoginResponse = z.infer<typeof loginResponseSchema>;

// ---------------------------------------------------------------------------
// POST /auth/refresh
// ---------------------------------------------------------------------------

export const refreshRequestSchema = z.object({
  refreshToken: z.string().min(1),
});
export type RefreshRequest = z.infer<typeof refreshRequestSchema>;

/**
 * Refresh rotates: the presented token is invalidated and a new pair returned.
 * Presenting an already-rotated token is treated as theft and revokes the whole
 * token family, which is why the response is a full pair rather than one token.
 */
export const refreshResponseSchema = tokenPairSchema;
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;

// ---------------------------------------------------------------------------
// POST /auth/logout
// ---------------------------------------------------------------------------

export const logoutRequestSchema = z.object({
  /** Omitted when the caller only holds an access token; the server then
   *  revokes the session bound to that access token. */
  refreshToken: z.string().min(1).optional(),
  allDevices: z.boolean().default(false),
});
export type LogoutRequest = z.infer<typeof logoutRequestSchema>;

export const logoutResponseSchema = okResponseSchema;
export type LogoutResponse = z.infer<typeof logoutResponseSchema>;

// ---------------------------------------------------------------------------
// GET /auth/me
// ---------------------------------------------------------------------------

export const meResponseSchema = z.object({
  user: authUserSummarySchema,
});
export type MeResponse = z.infer<typeof meResponseSchema>;

// ---------------------------------------------------------------------------
// POST /auth/verify-email  (stub in MVP)
// ---------------------------------------------------------------------------

export const verifyEmailRequestSchema = z.object({
  token: z.string().min(16).max(512),
});
export type VerifyEmailRequest = z.infer<typeof verifyEmailRequestSchema>;

export const verifyEmailResponseSchema = z.object({
  userId: idSchema,
  status: userStatusSchema,
  verifiedAt: isoDateTimeSchema.nullable(),
});
export type VerifyEmailResponse = z.infer<typeof verifyEmailResponseSchema>;
